import {
    Card,
    Colors,
    Heading,
    PageContainer,
    Text,
    TextInput,
    type OptionProps,
} from '@amelio/ui-library';

import { CYCLE_NAME_MAX_LENGTH } from '../validation';
import { AudienceEmployeeCount } from './AudienceEmployeeCount/AudienceEmployeeCount';
import { CycleAudienceSection } from './CycleAudienceSection/CycleAudienceSection';

import type { CycleAudienceTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { CompanyAttributeMap } from '@/utils/company';

import { FormField } from '@/components/FormField/FormField';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { StepFooter } from '@/navigation/shared/StepFooter';
import {
    CYCLE_CARD_SURFACE,
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';
import { CYCLE_DATE_FIELD_CHROME } from '@/pages/CycleEvaluation/components/cycleFieldChrome';
import { EvaluationDatePeriod } from '@/pages/ManagerAssessment/Evaluation/components/EvaluationDatePeriod';

const HEADING_TYPE = 'text-[20px] leading-[25px]';

const BODY_TYPE = 'text-[14px] leading-[20px]';

const HINT_TYPE = 'text-[12px] leading-[19.5px]';

const FIELD_STACK = 'flex flex-col gap-[20px]';
const FIELD_GROUP_OFFSET = 'mt-[15px]';

const FIELD_STACK_WIDTH = 'w-full max-w-[768px]';

const FIELD_CONTROL = [
    'h-[44px] rounded-[8px]',
    'border border-step-rail-track bg-white',
    'px-[12px] text-slate',
].join(' ');

const FIELD_LABEL = [
    'gap-[8px]!',
    '[&>div>label]:uppercase [&>div>label]:font-bold!',
    '[&>div>label]:text-[color:var(--color-cycle-form-label)]!',
    '[&>div>label]:text-[14px]!',
    '[&>div>label]:leading-[20px]!',
    '[&>div>label]:tracking-[-0.35px]!',
].join(' ');

const PERIOD_TEST_ID = 'cycle-evaluation-period';

export interface CycleInformationProps {
    isLoading: boolean;
    isReadOnly?: boolean;
    isAudienceHydrated?: boolean;
    name: string;
    nameErrorMessage?: string;
    periodErrorMessage?: string;
    audienceErrorMessage?: string;
    selectedPeriod: string;
    periods: EvaluationPeriodOption[];
    periodStart?: Date;
    periodEnd?: Date;
    audienceType: CycleAudienceTypeValue;
    companyAttributes: OptionProps[];
    selectedCompanyAttributes: CompanyAttributeMap;
    filters: SelectedFilters;
    resolvedEmployeeCount: number;
    onNameChange: (name: string) => void;
    onPeriodChange: (selectedPeriod: string) => void;
    onPeriodStartChange: (date?: Date) => void;
    onPeriodEndChange: (date?: Date) => void;
    onAudienceTypeChange: (audienceType: CycleAudienceTypeValue) => void;
    onSelectCompanyAttribute: (value: string) => void;
    onSelectCompanyAttributeOption: (params: OnSelectCompanyAttributeOption) => void;
}

export const CycleInformation = ({
    isLoading,
    isReadOnly = false,
    isAudienceHydrated = true,
    name,
    nameErrorMessage,
    periodErrorMessage,
    audienceErrorMessage,
    selectedPeriod,
    periods,
    periodStart,
    periodEnd,
    audienceType,
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    resolvedEmployeeCount,
    onNameChange,
    onPeriodChange,
    onPeriodStartChange,
    onPeriodEndChange,
    onAudienceTypeChange,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
}: CycleInformationProps) => {
    const { t } = useAmelioTranslations();

    return (
        <PageContainer className={'cycle-evaluation-group-section'}>
            <Card
                padding={'none'}
                className={CYCLE_CARD_SURFACE}
                bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
                borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            >
                <Heading
                    level={3}
                    color={Colors.SLATE}
                    className={HEADING_TYPE}
                >
                    {t('cycleEvaluationCycleInformation.header.heading')}
                </Heading>

                <Text
                    color={Colors.STEP_RAIL_MUTED}
                    className={`${BODY_TYPE} mt-[12px]`}
                >
                    {t('cycleEvaluationCycleInformation.header.description')}
                </Text>

                {isLoading ? (
                    <Skeleton className={`${FIELD_STACK} ${FIELD_STACK_WIDTH} mt-[36px]`}>
                        <Bone className={'h-12 w-full'} />

                        <Bone className={'h-24 w-full'} />

                        <Bone className={'h-24 w-64'} />
                    </Skeleton>
                ) : (
                    <div className={`${FIELD_STACK} ${FIELD_STACK_WIDTH} mt-[36px]`}>
                        <FormField
                            label={t('cycleEvaluationCycleInformation.name.label')}
                            id={'cycle-name'}
                            dataTestId={'cycle-name-field'}
                            errorMessage={nameErrorMessage}
                            className={FIELD_LABEL}
                        >
                            <TextInput
                                id={'cycle-name'}
                                value={name}
                                placeholder={t('cycleEvaluationCycleInformation.name.placeholder')}
                                maxLength={CYCLE_NAME_MAX_LENGTH}
                                error={Boolean(nameErrorMessage)}
                                readOnly={isReadOnly}
                                onChange={onNameChange}
                                className={FIELD_CONTROL}
                                dataTestId={'cycle-name-input'}
                            />
                        </FormField>

                        <FormField
                            label={t('cycleEvaluationCycleInformation.period.label')}
                            dataTestId={'cycle-period-field'}
                            errorMessage={periodErrorMessage}
                            className={`${FIELD_LABEL} ${FIELD_GROUP_OFFSET}`}
                        >
                            <EvaluationDatePeriod
                                selectedPeriod={selectedPeriod}
                                periods={periods}
                                onPeriodChange={onPeriodChange}
                                fromDate={periodStart}
                                toDate={periodEnd}
                                onFromDateChange={onPeriodStartChange}
                                onToDateChange={onPeriodEndChange}
                                readOnly={isReadOnly}
                                dataTestId={PERIOD_TEST_ID}
                                surface={'flat'}
                                customRange={{
                                    appearance: CYCLE_DATE_FIELD_CHROME,
                                    fromPlaceholder: t(
                                        'cycleEvaluationCycleInformation.period.startPlaceholder',
                                    ),
                                    toPlaceholder: t(
                                        'cycleEvaluationCycleInformation.period.endPlaceholder',
                                    ),
                                    prefillsOnSelect: false,
                                    enforcesChronology: true,
                                }}
                            />

                            <Text
                                color={Colors.STEP_RAIL_MUTED}
                                className={`${HINT_TYPE} mt-[8px]`}
                            >
                                {t('cycleEvaluationCycleInformation.period.hint')}
                            </Text>
                        </FormField>

                        <FormField
                            label={t('cycleEvaluationCycleInformation.audience.label')}
                            dataTestId={'cycle-audience-field'}
                            errorMessage={audienceErrorMessage}
                            className={`${FIELD_LABEL} ${FIELD_GROUP_OFFSET}`}
                        >
                            <CycleAudienceSection
                                isReadOnly={isReadOnly}
                                isAudienceHydrated={isAudienceHydrated}
                                audienceType={audienceType}
                                companyAttributes={companyAttributes}
                                selectedCompanyAttributes={selectedCompanyAttributes}
                                filters={filters}
                                onAudienceTypeChange={onAudienceTypeChange}
                                onSelectCompanyAttribute={onSelectCompanyAttribute}
                                onSelectCompanyAttributeOption={onSelectCompanyAttributeOption}
                            />

                            <AudienceEmployeeCount count={resolvedEmployeeCount} />
                        </FormField>
                    </div>
                )}

                <StepFooter />
            </Card>
        </PageContainer>
    );
};
